/**
 * Assortment (pack size) helpers shared by every app.
 *
 * A SKU may be sold in several assortments, e.g. "Pack of 10 / 20 / 100" or "1 kg / 10 kg / 25 kg".
 * Each assortment has its own price and consumes `unitsPerPack` base units from the SKU's shared stock.
 * SKUs without assortments keep behaving exactly as before (single unit, SKU price).
 */

export interface AssortmentPack {
  id?: number | null;
  /** Distinct child variant SKU ID for warehouse barcode scan, order tracking, and individual return debit notes. */
  skuId?: number | null;
  /** Distinct variant SKU Code, e.g. "RICE-SM-25K-P1" or "AASH-ATT-10K". */
  skuCode?: string | null;
  /** Barcode / EAN-13 / GTIN for this physical pack variant. */
  barcode?: string | null;
  label: string;
  packSize?: number | null;
  packUnit?: string | null;
  unitsPerPack?: number | null;
  pricePaise: number;
  supplyPricePaise?: number | null;
  mrpPaise?: number | null;
  weightKg?: number | null;
  stock?: number | null;
  isDefault?: boolean | null;
  active?: boolean | null;
  displayOrder?: number | null;
}

export interface AssortmentSkuLike {
  pricePaise: number;
  mrpPaise?: number | null;
  weightKg?: number | null;
  availableQty: number;
  packs?: AssortmentPack[] | null;
}

export const PACKAGING_OPTIONS = [
  { value: "pack", label: "Pack of", prefix: "Pack of" },
  { value: "set", label: "Set of", prefix: "Set of" },
  { value: "pouch", label: "Pouch of", prefix: "Pouch of" },
  { value: "box", label: "Box of", prefix: "Box of" },
  { value: "bag", label: "Bag of", prefix: "Bag of" },
  { value: "carton", label: "Carton of", prefix: "Carton of" },
  { value: "case", label: "Case of", prefix: "Case of" },
  { value: "tin", label: "Tin of", prefix: "Tin of" },
  { value: "bottle", label: "Bottle of", prefix: "Bottle of" },
  { value: "strip", label: "Strip of", prefix: "Strip of" },
  { value: "bundle", label: "Bundle of", prefix: "Bundle of" },
  { value: "direct", label: "Direct (e.g. 10 kg, 1 L)", prefix: "" },
] as const;

export type PackagingOptionValue = (typeof PACKAGING_OPTIONS)[number]["value"];

export const PACK_UNITS = [
  "pcs",
  "kg",
  "g",
  "L",
  "ml",
  "pouch",
  "set",
  "pack",
  "bag",
  "box",
  "carton",
  "dozen",
] as const;

export interface AssortmentPreset {
  name: string;
  packs: Array<Pick<AssortmentPack, "label" | "packSize" | "packUnit" | "unitsPerPack">>;
}

/** Quick-fill presets for catalog creation. Values are fully editable afterwards. */
export const ASSORTMENT_PRESETS: AssortmentPreset[] = [
  {
    name: "Pack of 10 / 20 / 100",
    packs: [10, 20, 100].map((n) => ({ label: `Pack of ${n}`, packSize: n, packUnit: "pcs", unitsPerPack: n })),
  },
  {
    name: "Set of 2 / 5 / 10",
    packs: [2, 5, 10].map((n) => ({ label: `Set of ${n}`, packSize: n, packUnit: "pcs", unitsPerPack: n })),
  },
  {
    name: "Pouch of 5 / 10 / 20",
    packs: [5, 10, 20].map((n) => ({ label: `Pouch of ${n}`, packSize: n, packUnit: "pcs", unitsPerPack: n })),
  },
  {
    name: "Box of 10 / 20 / 50",
    packs: [10, 20, 50].map((n) => ({ label: `Box of ${n}`, packSize: n, packUnit: "pcs", unitsPerPack: n })),
  },
  {
    name: "1 / 10 / 25 / 50 kg Bags",
    packs: [1, 10, 25, 50].map((n) => ({ label: `${n} kg`, packSize: n, packUnit: "kg", unitsPerPack: n })),
  },
  {
    name: "1 / 5 / 15 L Tins/Cans",
    packs: [1, 5, 15].map((n) => ({ label: `${n} L`, packSize: n, packUnit: "L", unitsPerPack: n })),
  },
];

/** Infers the packaging prefix from an existing label or unit. */
export function inferPackagingType(label?: string | null, packUnit?: string | null): PackagingOptionValue {
  const l = (label || "").toLowerCase().trim();
  if (l.startsWith("set of") || l.startsWith("set")) return "set";
  if (l.startsWith("pouch of") || l.startsWith("pouch")) return "pouch";
  if (l.startsWith("box of") || l.startsWith("box")) return "box";
  if (l.startsWith("bag of") || l.startsWith("bag")) return "bag";
  if (l.startsWith("carton of") || l.startsWith("carton")) return "carton";
  if (l.startsWith("case of") || l.startsWith("case")) return "case";
  if (l.startsWith("tin of") || l.startsWith("tin")) return "tin";
  if (l.startsWith("bottle of") || l.startsWith("bottle")) return "bottle";
  if (l.startsWith("strip of") || l.startsWith("strip")) return "strip";
  if (l.startsWith("bundle of") || l.startsWith("bundle")) return "bundle";
  if (l.startsWith("pack of") || l.startsWith("pack")) return "pack";

  const u = (packUnit || "").toLowerCase().trim();
  if (u === "kg" || u === "g" || u === "l" || u === "ml") return "direct";
  if (u === "set") return "set";
  if (u === "pouch") return "pouch";
  if (u === "box") return "box";
  if (u === "bag") return "bag";
  return "pack";
}

/** Builds a buyer-facing label from size + unit + packaging option, e.g. (10, "pcs", "set") → "Set of 10", (10, "pcs", "pouch") → "Pouch of 10". */
export function buildPackLabel(
  size?: number | null,
  unit?: string | null,
  packagingType?: string | null
): string {
  if (!size || size <= 0) return "";
  const u = (unit || "").trim();
  const pkg = (packagingType || "").toLowerCase().trim();

  if (pkg === "set") return `Set of ${size}`;
  if (pkg === "pouch") return `Pouch of ${size}`;
  if (pkg === "box") return `Box of ${size}`;
  if (pkg === "pack") return `Pack of ${size}`;
  if (pkg === "strip") return `Strip of ${size}`;
  if (pkg === "bundle") return `Bundle of ${size}`;
  if (pkg === "carton") return `Carton of ${size}`;
  if (pkg === "case") return `Case of ${size}`;
  if (pkg === "tin") return u && u !== "pcs" ? `Tin of ${size} ${u}` : `Tin of ${size} L`;
  if (pkg === "bottle") return u && u !== "pcs" ? `Bottle of ${size} ${u}` : `Bottle of ${size} L`;
  if (pkg === "bag") return u && u !== "pcs" ? `Bag of ${size} ${u}` : `Bag of ${size} kg`;

  if (pkg === "direct" || u === "kg" || u === "g" || u === "l" || u === "ml") {
    return `${size} ${u || "kg"}`;
  }
  if (u === "dozen") return `${size} dozen`;
  if (!u || u === "pcs") return `Pack of ${size}`;
  return `${size} ${u}`;
}

/** Converts a pack size into kilograms where the unit is a weight/volume unit (L≈kg). */
export function packWeightKg(pack: AssortmentPack): number | null {
  if (pack.weightKg && pack.weightKg > 0) return pack.weightKg;
  const size = pack.packSize ?? 0;
  if (size <= 0) return null;
  switch ((pack.packUnit || "").toLowerCase()) {
    case "kg":
    case "l":
      return size;
    case "g":
    case "ml":
      return Math.round((size / 1000) * 1000) / 1000;
    default:
      return null;
  }
}

export function activePacks(sku: Pick<AssortmentSkuLike, "packs">): AssortmentPack[] {
  return (sku.packs ?? [])
    .filter((p) => p.active !== false)
    .slice()
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
}

export function hasAssortments(sku: Pick<AssortmentSkuLike, "packs">): boolean {
  return activePacks(sku).length > 0;
}

export function defaultPack(sku: Pick<AssortmentSkuLike, "packs">): AssortmentPack | null {
  const packs = activePacks(sku);
  return packs.find((p) => p.isDefault) ?? packs[0] ?? null;
}

/** Resolves the pack to use for a SKU; falls back to the default pack when id is missing/unknown. */
export function resolvePack(sku: Pick<AssortmentSkuLike, "packs">, packId?: number | null): AssortmentPack | null {
  const packs = activePacks(sku);
  if (packs.length === 0) return null;
  if (packId != null) {
    const found = packs.find((p) => p.id === packId);
    if (found) return found;
  }
  return defaultPack(sku);
}

export function unitsPerPack(pack?: AssortmentPack | null): number {
  return pack?.unitsPerPack && pack.unitsPerPack > 0 ? pack.unitsPerPack : 1;
}

/** Effective selling price, MRP, weight and orderable qty for a SKU in a given assortment. */
export function packView(sku: AssortmentSkuLike, pack: AssortmentPack | null) {
  if (!pack) {
    return {
      pricePaise: sku.pricePaise,
      mrpPaise: sku.mrpPaise ?? null,
      weightKg: sku.weightKg ?? null,
      availableQty: sku.availableQty,
      unitsPerPack: 1,
      label: null as string | null,
    };
  }
  const per = unitsPerPack(pack);
  return {
    pricePaise: pack.pricePaise > 0 ? pack.pricePaise : sku.pricePaise * per,
    mrpPaise: pack.mrpPaise ?? null,
    weightKg: packWeightKg(pack),
    availableQty: Math.floor(Math.max(0, sku.availableQty) / per),
    unitsPerPack: per,
    label: pack.label,
  };
}

/** Cheapest active pack price — used for "from ₹X" display. */
export function minPackPricePaise(sku: AssortmentSkuLike): number {
  const packs = activePacks(sku);
  if (packs.length === 0) return sku.pricePaise;
  return Math.min(...packs.map((p) => packView(sku, p).pricePaise));
}

/** Stable key for a cart line (SKU + optional assortment). */
export function cartLineKey(skuId: number, packId?: number | null): string {
  return packId != null ? `${skuId}:${packId}` : `${skuId}`;
}

export function parseCartLineKey(key: string): { skuId: number; packId: number | null } {
  const [s, p] = key.split(":");
  return { skuId: Number(s), packId: p != null && p !== "" ? Number(p) : null };
}
